import { selectTreatmentWithConfig } from '@splitsoftware/splitio-redux';
import { useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { STORAGE_KEY, IN_MEMORY_CACHE } from '../react/consts.js';
const useRemoteSplit = (splitName) => {
    return useSelector((state) => {
        const splitData = selectTreatmentWithConfig(state.split, splitName, undefined, 
        // this way we do not get a inbetween split object back where ready would be true
        // but the treatment is still "control" because split-io response is not yet available
        // @ts-expect-error -- see above
        null);
        return state.split.isReady && splitData != null
            ? {
                ready: true,
                treatment: splitData.treatment,
                config: String(splitData.config), // always return a string to make a later comparison for change easier
            }
            : {
                ready: state.split.isTimedout, // edge case to make sure we do not wait forever when split runs in timeout
                treatment: 'control',
                config: 'null',
            };
    }, shallowEqual);
};
const isSplitData = (data) => {
    return (Array.isArray(data) &&
        typeof data[0] === 'string' &&
        typeof data[1] === 'string');
};
const getCachedSplitData = () => {
    // prevent unnecessary localStorage reads
    if (useSplit[IN_MEMORY_CACHE] !== null) {
        return useSplit[IN_MEMORY_CACHE];
    }
    try {
        const rawCachedSplitData = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
        const parsedCachedSplitData = rawCachedSplitData != null &&
            typeof rawCachedSplitData === 'object' &&
            !Array.isArray(rawCachedSplitData)
            ? rawCachedSplitData
            : {};
        useSplit[IN_MEMORY_CACHE] = parsedCachedSplitData;
        return parsedCachedSplitData;
    }
    catch {
        // noop
    }
    return {};
};
const getSplitFromCache = (splitName) => {
    try {
        const cachedSplitData = getCachedSplitData();
        if (splitName in cachedSplitData) {
            const splitFromCache = cachedSplitData[splitName];
            if (isSplitData(splitFromCache)) {
                return {
                    ready: true,
                    treatment: splitFromCache[0],
                    config: splitFromCache[1],
                };
            }
        }
    }
    catch {
        // noop
    }
    return null;
};
const updateCachedSplitData = (splitName, split) => {
    if (useSplit[IN_MEMORY_CACHE]) {
        useSplit[IN_MEMORY_CACHE][splitName] = [
            split.treatment,
            String(split.config),
        ];
    }
    try {
        const cachedSplitData = getCachedSplitData();
        cachedSplitData[splitName] = [split.treatment, String(split.config)];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedSplitData));
    }
    catch {
        // noop
    }
};
const useCachedSplit = (splitName) => useMemo(() => {
    return getSplitFromCache(splitName);
}, [splitName]);
/**
 * React hook to get split data for a specific split.
 *
 * This hook will first return a locally cached split data if available.
 * But when the remote split data is ready, it will return that instead.
 * And update the local cache with the remote data if it differs.
 *
 * The `useSplit[IN_MEMORY_CACHE]` is used to avoid unnecessary localStorage reads and writes.
 * It is accessible as a static property on the hook function to allow for resetting the cache in-between tests.
 * **Using it programmatically in your application is discouraged**!
 *
 * @param splitName The name of split as defined in split.io
 * @returns `{ready: boolean, treatment: string, config: null | object}`
 */
export const useSplit = (splitName) => {
    const rawRemoteSplit = useRemoteSplit(splitName);
    const rawCachedSplit = useCachedSplit(splitName);
    // split.io result is ready to use and differs from the cached split?
    // write the result to localStorage for later / different page loads
    useEffect(() => {
        if (rawRemoteSplit.ready &&
            (!rawCachedSplit ||
                rawRemoteSplit.treatment !== rawCachedSplit.treatment ||
                rawRemoteSplit.config !== rawCachedSplit.config)) {
            updateCachedSplitData(splitName, rawRemoteSplit);
        }
    }, [rawCachedSplit, rawRemoteSplit, splitName]);
    return useMemo(() => {
        // only return local cached split data
        // when local data is available BUT remote data is not ready yet
        if (rawCachedSplit && !rawRemoteSplit.ready) {
            try {
                return {
                    ...rawCachedSplit,
                    config: JSON.parse(rawCachedSplit.config),
                };
            }
            catch {
                // json parse on locally cached split.config data failed
            }
        }
        if (rawRemoteSplit.ready) {
            try {
                return {
                    ...rawRemoteSplit,
                    config: JSON.parse(rawRemoteSplit.config),
                };
            }
            catch {
                // json parse on remote split.config data failed
                // we return a ready but control treament group split here to avoid that this never becomes ready
                return {
                    config: null,
                    ready: true,
                    treatment: 'control',
                };
            }
        }
        // this default is also return by split-io sdk but we want to be explicit here
        return {
            config: null,
            ready: false,
            treatment: 'control',
        };
    }, [rawCachedSplit, rawRemoteSplit]);
};
useSplit[IN_MEMORY_CACHE] = null;
//# sourceMappingURL=useSplit.js.map